
import React from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';

interface ClearConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  clearType: 'week' | 'month' | 'custom' | 'missing';
  startDate: Date;
  endDate: Date;
  isClearing?: boolean;
}

const ClearConfirmationDialog: React.FC<ClearConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  clearType,
  startDate,
  endDate,
  isClearing = false
}) => {
  const { t } = useLanguage();

  const getTitle = () => {
    switch (clearType) {
      case 'week':
        return t("clearWeek") || "Clear Week";
      case 'month':
        return t("clearMonth") || "Clear Month";
      case 'custom':
        return t("clearCustomRange") || "Clear Custom Range";
      case 'missing':
        return t("clearMissingShifts") || "Clear Missing Shifts";
      default:
        return t("clearRange") || "Clear Range";
    }
  };

  const getDescription = () => {
    const dateRange = `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
    
    switch (clearType) {
      case 'week':
        return t("clearWeekConfirmation") || `Are you sure you want to clear all shifts for the week of ${dateRange}? This action cannot be undone.`;
      case 'month':
        return t("clearMonthConfirmation") || `Are you sure you want to clear all shifts for the month from ${dateRange}? This action cannot be undone.`;
      case 'custom':
        return t("clearCustomRangeConfirmation") || `Are you sure you want to clear all shifts in the selected range ${dateRange}? This action cannot be undone.`;
      case 'missing':
        return t("clearMissingShiftsConfirmation") || `Are you sure you want to clear all missing (unassigned) shifts for the week of ${dateRange}? This action cannot be undone.`;
      default:
        return t("clearRangeConfirmation") || `Are you sure you want to clear all shifts from ${dateRange}? This action cannot be undone.`;
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{getTitle()}</AlertDialogTitle>
          <AlertDialogDescription>
            {getDescription()}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isClearing}>
            {t("cancel") || "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            disabled={isClearing}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isClearing ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                {t("clearing") || "Clearing..."}
              </>
            ) : (
              t("clear") || "Clear"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ClearConfirmationDialog;
